import { useState, useEffect } from 'react';
import { EllipsisVerticalIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

interface AdminAction {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  variant?: 'default' | 'danger';
}

interface AdminActionsBarProps {
  title: string;
  subtitle: string;
  actions: AdminAction[];
  isAdmin: boolean;
  className?: string;
}

export default function AdminActionsBar({ 
  title, 
  subtitle, 
  actions, 
  isAdmin, 
  className = "mb-6" 
}: AdminActionsBarProps) {
  const [showActions, setShowActions] = useState(false);

  // Close actions when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showActions) {
        setShowActions(false);
      }
    };

    if (showActions) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showActions]);

  if (!isAdmin) {
    return null;
  }

  return (
    <div className={`bg-white shadow-md rounded-lg border border-gray-200 ${className}`}>
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowActions(!showActions);
              }}
              className="p-1.5 rounded-full border-2 border-primary hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
              type="button"
            >
              <EllipsisVerticalIcon className="h-4 w-4 text-primary stroke-2" />
            </button>
            
            {showActions && (
              <div 
                className="absolute right-0 mt-2 w-56 rounded-lg shadow-2xl bg-white border border-gray-200 z-[100] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                style={{ boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.1)' }}
              >
                <div className="py-2">
                  {actions.map((action, index) => {
                    const IconComponent = action.icon || PencilSquareIcon;
                    const isDanger = action.variant === 'danger';
                    const isLastNonDanger = index === actions.length - 2 && actions[index + 1]?.variant === 'danger';
                    
                    return (
                      <>
                        <button
                          key={index}
                          onClick={() => {
                            action.onClick();
                            setShowActions(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm flex items-center transition-colors ${
                            isDanger 
                              ? 'text-red-600 hover:bg-red-50' 
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                          type="button"
                        >
                          <IconComponent className={`h-4 w-4 mr-3 ${
                            isDanger ? 'text-red-600' : 'text-gray-500'
                          }`} />
                          {action.label}
                        </button>
                        {isLastNonDanger && <div className="border-t border-gray-100 my-1"></div>}
                      </>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
